import { useEffect, useState } from "react";
import { Calendar, Clock, Loader2, Plus, Save, Tag, Trash2, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { RegistrationFieldsManager } from "@/pages/admin/AdminEventRegistration";

// Timeslot bookings ("Sinterklaas huisbezoeken"-style events) — admin section
// for managing event_timeslots: a bulk generator (so an admin never has to add
// dozens/hundreds of individual slots by hand) plus a manual per-slot add/edit/
// delete table, grouped by date — same self-fetching, manager+row idiom as
// EventDaysManager/DayRow in AdminEventRegistration.jsx. Also manages
// timeslot_categories — per-event rate cards ("Bedrijven" €100/60min,
// "Particulieren Dronten" €55/20min, ...) that fix a generated slot's own
// duration AND price; a category is purely a naming/price/duration tag at
// generation time, never read by checkout/reservation/payment itself.
//
// Also renders the shared RegistrationFieldsManager (imported from
// AdminEventRegistration.jsx, not duplicated) for admin-defined custom
// checkout fields -- event_registration_fields is a generic table the backend
// (_load_multiday_field_defs, used from BOTH checkout modes) already reads
// for timeslot bookings with no "multi_day_registration_enabled" gate at all;
// this tab is simply the only place a timeslot-booking event could reach it
// from, since AdminEventForm.jsx's "Aanmeldformulier" section (which also
// shows EventDaysManager/event_days -- irrelevant here) only appears when
// multi_day_registration_enabled is on, and that flag is mutually exclusive
// with timeslot_booking_enabled.

function centsToEuroInput(cents) {
  return ((cents || 0) / 100).toFixed(2);
}

function euroInputToCents(value) {
  const n = Number.parseFloat(String(value).replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

const WEEKDAY_LABELS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

function CategoryRow({ category, authAxios, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: category.name || "",
    price: centsToEuroInput(category.price_cents),
    duration_minutes: String(category.duration_minutes ?? 30),
    active: category.active !== false,
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setSaving(true);
    try {
      await authAxios.patch(`/admin/timeslot-categories/${category.id}`, {
        name: form.name.trim(),
        price_cents: euroInputToCents(form.price),
        duration_minutes: Math.max(1, Number(form.duration_minutes) || 30),
        active: form.active,
      });
      toast.success("Categorie opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Categorie "${category.name}" verwijderen? Bestaande tijdsloten blijven bestaan, maar verliezen deze categorie.`)) return;
    try {
      await authAxios.delete(`/admin/timeslot-categories/${category.id}`);
      toast.success("Categorie verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`timeslot-category-row-${category.id}`}>
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-white font-semibold">{category.name}</span>
          <span className="text-xs text-slate-300">{centsToEuroInput(category.price_cents)} € / {category.duration_minutes} min</span>
          {category.active === false && (
            <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold bg-slate-700 text-slate-400">Inactief</span>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} data-testid={`timeslot-category-edit-${category.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
            Bewerken
          </button>
          <button type="button" onClick={del} data-testid={`timeslot-category-delete-${category.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2" data-testid={`timeslot-category-editing-${category.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Naam (bv. Bedrijven)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm sm:col-span-2" />
        <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" step="0.01" min="0" placeholder="Prijs (€)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        <input value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} type="number" min="1" placeholder="Duur (min)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        Actief
      </label>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

function TimeslotCategoriesManager({ eventId, authAxios, categories, loading, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", price: "0.00", duration_minutes: "30" });

  const create = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/timeslot-categories`, {
        name: form.name.trim(),
        price_cents: euroInputToCents(form.price),
        duration_minutes: Math.max(1, Number(form.duration_minutes) || 30),
      });
      toast.success("Categorie toegevoegd");
      setForm({ name: "", price: "0.00", duration_minutes: "30" });
      setAdding(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Categorieën</div>
          <span className="text-xs text-slate-400">({categories.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="timeslot-category-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Categorie
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Een categorie is een tarief: naam, prijs en duur — bv. "Bedrijven" (€100 per uur), "Verenigingen en
        stichtingen" (€50 per half uur), "Particulieren Dronten" (€55 per 20 minuten). Kies een categorie bij het
        genereren van tijdsloten om duur én prijs automatisch in te vullen.
      </p>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && categories.length === 0 && !adding && (
        <div className="text-sm text-slate-400">Nog geen categorieën — zonder categorie vul je prijs en duur zelf in bij het genereren.</div>
      )}

      {adding && (
        <div className="mb-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Naam (bv. Bedrijven)" data-testid="timeslot-category-name" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm sm:col-span-2" />
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" step="0.01" min="0" placeholder="Prijs (€)" data-testid="timeslot-category-price" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
            <input value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} type="number" min="1" placeholder="Duur (min)" data-testid="timeslot-category-duration" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="timeslot-category-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {categories.map((c) => (
          <CategoryRow key={c.id} category={c} authAxios={authAxios} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function TimeslotRow({ slot, categoriesById, authAxios, onChanged, selected, onToggleSelect }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: slot.date || "",
    start_time: (slot.start_time || "").slice(0, 5),
    end_time: (slot.end_time || "").slice(0, 5),
    price: centsToEuroInput(slot.price_cents),
    capacity: String(slot.capacity ?? 1),
    active: slot.active !== false,
  });

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/admin/timeslots/${slot.id}`, {
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        price_cents: euroInputToCents(form.price),
        capacity: Math.max(1, Number(form.capacity) || 1),
        active: form.active,
      });
      toast.success("Tijdslot opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Tijdslot ${form.start_time} - ${form.end_time} op ${form.date} verwijderen?`)) return;
    try {
      await authAxios.delete(`/admin/timeslots/${slot.id}`);
      toast.success("Tijdslot verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const booked = slot.booked_count || 0;
  const full = booked >= slot.capacity;
  const category = slot.category_id ? categoriesById[slot.category_id] : null;

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`timeslot-row-${slot.id}`}>
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <input
            type="checkbox"
            checked={selected}
            disabled={booked > 0}
            title={booked > 0 ? "Een tijdslot met boekingen kan niet geselecteerd worden voor verwijderen" : undefined}
            onChange={() => {}}
            onClick={(e) => {
              e.preventDefault();
              onToggleSelect(slot.id, e.shiftKey);
            }}
            data-testid={`timeslot-select-${slot.id}`}
            className="h-4 w-4 shrink-0 rounded border-svj-border bg-svj-bg disabled:opacity-30"
          />
          <span className="text-sm text-white font-semibold">
            {(slot.start_time || "").slice(0, 5)} - {(slot.end_time || "").slice(0, 5)}
          </span>
          <span className="text-xs text-slate-300">{centsToEuroInput(slot.price_cents)} €</span>
          {category && (
            <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold bg-svj-cyan/15 text-svj-cyan">
              {category.name}
            </span>
          )}
          <span
            className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold ${
              full ? "bg-red-500/20 text-red-300" : "bg-svj-border text-slate-300"
            }`}
            data-testid={`timeslot-occupancy-${slot.id}`}
          >
            {full ? "VOL" : `${booked} / ${slot.capacity}`}
          </span>
          {slot.active === false && (
            <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold bg-slate-700 text-slate-400">
              Inactief
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} data-testid={`timeslot-edit-${slot.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
            Bewerken
          </button>
          <button
            type="button"
            onClick={del}
            disabled={booked > 0}
            title={booked > 0 ? "Dit tijdslot heeft al boekingen — zet het liever op inactief" : undefined}
            data-testid={`timeslot-delete-${slot.id}`}
            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2" data-testid={`timeslot-editing-${slot.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
        <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} type="time" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} type="time" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" step="0.01" min="0" placeholder="Prijs (€)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="1" placeholder="Capaciteit" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <label className="h-9 flex items-center gap-2 px-3 rounded-lg bg-svj-bg border border-svj-border text-slate-300 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
          Actief
        </label>
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

function TimeslotGenerator({ eventId, authAxios, categories, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    date_from: "",
    date_to: "",
    weekdays: [0, 1, 2, 3, 4, 5, 6],
    start_time: "09:00",
    end_time: "17:00",
    category_id: "",
    slot_duration_minutes: "30",
    price: "0.00",
    capacity: "1",
    buffer_minutes: "0",
  });

  const selectedCategory = categories.find((c) => c.id === form.category_id) || null;

  const toggleWeekday = (d) => {
    setForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(d) ? prev.weekdays.filter((w) => w !== d) : [...prev.weekdays, d],
    }));
  };

  const generate = async () => {
    if (!form.date_from || !form.date_to) {
      toast.error("Kies een start- en einddatum");
      return;
    }
    if (!selectedCategory && (!form.slot_duration_minutes || !form.price)) {
      toast.error("Kies een categorie, of vul zelf een duur en prijs in");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/timeslots/generate`, {
        date_from: form.date_from,
        date_to: form.date_to,
        weekdays: form.weekdays.length < 7 ? form.weekdays : null,
        start_time: form.start_time,
        end_time: form.end_time,
        category_id: form.category_id || null,
        ...(selectedCategory
          ? {}
          : {
              slot_duration_minutes: Number(form.slot_duration_minutes) || 30,
              price_cents: euroInputToCents(form.price),
            }),
        capacity: Math.max(1, Number(form.capacity) || 1),
        buffer_minutes: Math.max(0, Number(form.buffer_minutes) || 0),
      });
      toast.success(`${data.created} tijdsloten aangemaakt`);
      setOpen(false);
      onGenerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="timeslot-generate-open"
        className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5"
      >
        <Wand2 className="h-3.5 w-3.5" /> Genereer tijdsloten
      </button>
    );
  }

  return (
    <div className="mb-4 px-4 py-4 rounded-lg bg-svj-bg/60 border border-svj-border space-y-3" data-testid="timeslot-generate-form">
      <div className="text-sm font-semibold text-white">Genereer tijdsloten</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-slate-400">Van datum</span>
          <input value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} type="date" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Tot datum</span>
          <input value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} type="date" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        </label>
      </div>
      <div>
        <span className="text-xs text-slate-400">Dagen van de week</span>
        <div className="mt-1 flex gap-1.5">
          {WEEKDAY_LABELS.map((label, d) => (
            <button
              key={d}
              type="button"
              onClick={() => toggleWeekday(d)}
              className={`h-8 w-9 rounded-lg border text-xs font-semibold ${
                form.weekdays.includes(d) ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan" : "bg-svj-bg border-svj-border text-slate-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-slate-400">Starttijd</span>
          <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} type="time" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Eindtijd</span>
          <input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} type="time" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        </label>
      </div>
      {categories.length > 0 && (
        <label className="block">
          <span className="text-xs text-slate-400">Categorie (optioneel — bepaalt duur + prijs automatisch)</span>
          <select
            value={form.category_id}
            onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            data-testid="timeslot-generate-category"
            className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
          >
            <option value="">Geen — zelf duur en prijs instellen</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({centsToEuroInput(c.price_cents)} € / {c.duration_minutes} min)
              </option>
            ))}
          </select>
        </label>
      )}
      {selectedCategory ? (
        <p className="text-xs text-svj-cyan">
          Elk tijdslot wordt {selectedCategory.duration_minutes} minuten lang en kost {centsToEuroInput(selectedCategory.price_cents)} € —
          afkomstig van de categorie "{selectedCategory.name}".
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs text-slate-400">Duur per blok (min)</span>
            <input value={form.slot_duration_minutes} onChange={(e) => setForm({ ...form, slot_duration_minutes: e.target.value })} type="number" min="1" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Prijs per blok (€)</span>
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" step="0.01" min="0" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
          </label>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-slate-400">Capaciteit per blok</span>
          <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="1" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Reistijd tussen afspraken (min)</span>
          <input
            value={form.buffer_minutes}
            onChange={(e) => setForm({ ...form, buffer_minutes: e.target.value })}
            type="number"
            min="0"
            placeholder="0"
            data-testid="timeslot-generate-buffer"
            className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
          />
        </label>
      </div>
      <p className="text-[11px] text-slate-500">
        Al bestaande tijdsloten op dezelfde datum en starttijd worden overgeslagen — nooit dubbel aangemaakt.
        {Number(form.buffer_minutes) > 0 && " Tussen elk tijdslot wordt automatisch de ingestelde reistijd vrijgehouden."}
      </p>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">
          Annuleren
        </button>
        <button type="button" onClick={generate} disabled={generating} data-testid="timeslot-generate-submit" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Genereren
        </button>
      </div>
    </div>
  );
}

function TimeslotAddForm({ eventId, authAxios, categories, onCreated }) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ date: "", start_time: "", end_time: "", price: "0.00", capacity: "1", category_id: "" });

  const create = async () => {
    if (!form.date || !form.start_time || !form.end_time) {
      toast.error("Datum, starttijd en eindtijd zijn verplicht");
      return;
    }
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/timeslots`, {
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        price_cents: euroInputToCents(form.price),
        capacity: Math.max(1, Number(form.capacity) || 1),
        category_id: form.category_id || null,
      });
      toast.success("Tijdslot toegevoegd");
      setForm({ date: "", start_time: "", end_time: "", price: "0.00", capacity: "1", category_id: "" });
      setAdding(false);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  const applyCategory = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) {
      setForm({ ...form, category_id: "" });
      return;
    }
    const start = form.start_time;
    let end = form.end_time;
    if (start) {
      const [h, m] = start.split(":").map(Number);
      const total = h * 60 + m + cat.duration_minutes;
      end = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
    }
    setForm({ ...form, category_id: categoryId, price: centsToEuroInput(cat.price_cents), end_time: end });
  };

  if (!adding) {
    return (
      <button type="button" onClick={() => setAdding(true)} data-testid="timeslot-add" className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-4 py-1.5 inline-flex items-center gap-1.5">
        <Plus className="h-3.5 w-3.5" /> Los tijdslot toevoegen
      </button>
    );
  }

  return (
    <div className="mb-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
      {categories.length > 0 && (
        <select
          value={form.category_id}
          onChange={(e) => applyCategory(e.target.value)}
          className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-full"
        >
          <option value="">Geen categorie</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({centsToEuroInput(c.price_cents)} € / {c.duration_minutes} min)</option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} type="time" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        <input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} type="time" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" step="0.01" min="0" placeholder="Prijs (€)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
        <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="1" placeholder="Capaciteit" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm" />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
        <button type="button" onClick={create} disabled={creating} data-testid="timeslot-add-submit" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
        </button>
      </div>
    </div>
  );
}

export default function AdminEventTimeslots({ eventId, authAxios, manualBookingsEnabled }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [fields, setFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastClickedId, setLastClickedId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/timeslots`);
      setSlots(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/timeslot-categories`);
      setCategories(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadFields = async () => {
    setLoadingFields(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/registration-fields`);
      setFields(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingFields(false);
    }
  };

  useEffect(() => {
    load();
    loadCategories();
    loadFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const categoriesById = Object.fromEntries(categories.map((c) => [c.id, c]));

  const byDate = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort();
  for (const date of dates) {
    byDate[date] = byDate[date].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  }
  // Flat, on-screen order -- exactly what a shift-click range spans between,
  // so "select the slots between here and there" always matches what's
  // actually visible (grouped by date, then by start time within a date).
  const orderedSlots = dates.flatMap((date) => byDate[date]);
  const selectableIds = orderedSlots.filter((s) => !(s.booked_count > 0)).map((s) => s.id);

  const toggleSelect = (id, shiftKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastClickedId) {
        const ids = orderedSlots.map((s) => s.id);
        const from = ids.indexOf(lastClickedId);
        const to = ids.indexOf(id);
        if (from !== -1 && to !== -1) {
          const [lo, hi] = from <= to ? [from, to] : [to, from];
          for (let i = lo; i <= hi; i++) {
            const slot = orderedSlots[i];
            if (!(slot.booked_count > 0)) next.add(slot.id);
          }
          setLastClickedId(id);
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setLastClickedId(id);
      return next;
    });
  };

  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allSelectableSelected) return new Set();
      return new Set(selectableIds);
    });
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} geselecteerde tijdslot${selectedIds.size === 1 ? "" : "en"} verwijderen?`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/timeslots/bulk-delete`, {
        timeslot_ids: Array.from(selectedIds),
      });
      const skipped = (data.skipped_booked || []).length;
      if (skipped > 0) {
        toast.error(`${data.deleted_count} verwijderd, ${skipped} overgeslagen (heeft al boekingen)`);
      } else {
        toast.success(`${data.deleted_count} tijdslot${data.deleted_count === 1 ? "" : "en"} verwijderd`);
      }
      setSelectedIds(new Set());
      setLastClickedId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <TimeslotCategoriesManager eventId={eventId} authAxios={authAxios} categories={categories} loading={loadingCategories} onChanged={loadCategories} />

      <div className="rounded-2xl border border-svj-border bg-svj-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-svj-cyan" />
            <div className="text-sm font-semibold text-white uppercase tracking-wider">Tijdsloten</div>
            <span className="text-xs text-slate-400">({slots.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <TimeslotAddForm eventId={eventId} authAxios={authAxios} categories={categories} onCreated={load} />
            <TimeslotGenerator eventId={eventId} authAxios={authAxios} categories={categories} onGenerated={load} />
          </div>
        </div>
        <p className="mb-3 text-xs text-slate-400">
          Elk tijdslot heeft zijn eigen datum, tijdsblok, prijs en capaciteit (meestal 1 — één afspraak per blok).
          Klanten kiezen bij het afrekenen eerst een categorie (indien aanwezig) en daarna precies één tijdslot per ticket.
        </p>
        {manualBookingsEnabled && (
          <p className="mb-3 text-xs text-svj-cyan">
            Handmatige blokkering is ingeschakeld — je kunt een "al gemaakte afspraak" direct toevoegen via
            "Handmatig ticket aanmaken" op het tabblad Tickets &amp; verkoop.
          </p>
        )}

        {loading && (
          <div className="text-sm text-slate-400 inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden…
          </div>
        )}
        {!loading && slots.length === 0 && (
          <div className="text-sm text-slate-400">Nog geen tijdsloten voor dit event.</div>
        )}
        {!loading && slots.length > 0 && (
          <div className="mb-3 flex items-center justify-between gap-2 flex-wrap rounded-lg border border-svj-border bg-svj-bg/40 px-3 py-2">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={allSelectableSelected}
                onChange={toggleSelectAll}
                disabled={selectableIds.length === 0}
                data-testid="timeslot-select-all"
                className="h-4 w-4 rounded border-svj-border bg-svj-bg"
              />
              Alles selecteren
              {selectedIds.size > 0 && (
                <span className="text-slate-500">
                  — {selectedIds.size} geselecteerd (shift+klik voor een aaneengesloten reeks)
                </span>
              )}
            </label>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid="timeslot-bulk-delete"
                className="rounded-lg border border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5"
              >
                {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Verwijder {selectedIds.size} geselecteerde{selectedIds.size === 1 ? "" : "n"}
              </button>
            )}
          </div>
        )}
        <div className="space-y-4">
          {dates.map((date) => (
            <div key={date}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 mb-1.5">
                <Clock className="h-3.5 w-3.5 text-svj-cyan" />
                {new Date(`${date}T00:00:00`).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
              <div className="space-y-2">
                {byDate[date].map((s) => (
                  <TimeslotRow
                    key={s.id}
                    slot={s}
                    categoriesById={categoriesById}
                    authAxios={authAxios}
                    onChanged={load}
                    selected={selectedIds.has(s.id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <RegistrationFieldsManager eventId={eventId} authAxios={authAxios} fields={fields} loading={loadingFields} onChanged={loadFields} categories={categories} />
    </div>
  );
}
